import React, { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MathText } from '@/components/MathText';
import {
  appendInlineHtml,
  sanitizeRichTextHtml,
  truncateHtml,
} from '@/utils/html';
import { hasLatex } from '@/utils/math';
import { stripHtml } from '@/utils/text';

const TEXT_LENGTH_LIMIT = 150;
const TOGGLE_BUTTON_CLASS = 'diary-text-toggle';

interface Props {
  text: string;
  isFullText?: boolean;
}

export const DiaryText: React.FC<Props> = ({ text, isFullText = false }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [textHeight, setTextHeight] = useState<number>();
  const textRef = useRef<HTMLDivElement>(null);

  const plainText = stripHtml(text);
  const isClamped = !isFullText && plainText.length > TEXT_LENGTH_LIMIT;
  const safeHtml = sanitizeRichTextHtml(text);
  const visibleHtml = isExpanded
    ? safeHtml
    : truncateHtml({ html: safeHtml, maxLength: TEXT_LENGTH_LIMIT });
  const toggleLabel = isExpanded ? t('global.showLess') : t('global.showMore');
  const clampedHtml = appendInlineHtml(
    visibleHtml,
    `<button type="button" class="${TOGGLE_BUTTON_CLASS} cursor-pointer font-medium text-primary">${toggleLabel}</button>`,
  );

  const toggleOnButtonClick = (event: MouseEvent) => {
    const isToggleClick = !!(event.target as Element).closest(
      `.${TOGGLE_BUTTON_CLASS}`,
    );

    if (isToggleClick) {
      setIsExpanded(!isExpanded);
    }
  };

  useEffect(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver(() =>
      setTextHeight(element.scrollHeight),
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isClamped]);

  if (hasLatex(plainText)) {
    return (
      <MathText text={plainText} className="text-body-14 min-w-0 flex-1" />
    );
  }

  if (!isClamped) {
    return (
      <div
        className="rich-text text-body-14 min-w-0 flex-1"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <div
        style={{ height: textHeight }}
        className="overflow-hidden transition-[height] duration-200 ease-out"
      >
        <div
          ref={textRef}
          onClick={toggleOnButtonClick}
          className="rich-text text-body-14"
          dangerouslySetInnerHTML={{ __html: clampedHtml }}
        />
      </div>
    </div>
  );
};
